import React, { useState, useEffect } from 'react';
import { Bell, Car, ChevronLeft, ChevronRight, MapPin, TrendingUp } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import MapView from '../components/MapView';
import VehiclePanel from '../components/VehiclePanel';
import AlertsPanel from '../components/AlertsPanel';
import { useVehicleStore, useAlertStore } from '../store';
import { vehicleService, alertService } from '../services/api';
import websocket from '../services/websocket';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const { vehicles, setVehicles, setLoading } = useVehicleStore();
  const { setAlerts, setUnreadCount, addAlert } = useAlertStore();
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    offline: 0,
    alerts: 0
  });

  useEffect(() => {
    loadData();
    setupWebSocket();

    return () => {
      websocket.disconnect();
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Carregar veículos
      const vehiclesRes = await vehicleService.getAll();
      setVehicles(vehiclesRes.data.data);

      // Carregar alertas
      const alertsRes = await alertService.getAll({ limit: 50 });
      setAlerts(alertsRes.data.data);

      // Carregar contagem de alertas não lidos
      const countRes = await alertService.getUnreadCount();
      const unreadCount = countRes.data.data.count;
      setUnreadCount(unreadCount);

      // Calcular estatísticas
      calculateStats(vehiclesRes.data.data, unreadCount);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (vehiclesList, alertsCount = 0) => {
    const total = vehiclesList.length;
    const active = vehiclesList.filter(v => v.is_active && !v.is_blocked).length;
    const offline = vehiclesList.filter(v => !v.is_active || v.is_blocked).length;
    
    setStats({
      total,
      active,
      offline,
      alerts: alertsCount
    });
  };

  const setupWebSocket = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    if (user) {
      websocket.connect(user.id);

      // Listener para novos alertas
      websocket.on('new_alert', (alert) => {
        addAlert(alert);
        toast.warning(alert.title);
      });

      // Listener para atualização de posições
      websocket.on('position_update', (data) => {
        // Atualizar posição do veículo no store
        console.log('Posição atualizada:', data);
      });
    }
  };

  return (
    <div className="flex h-screen bg-dark-950">
      <Sidebar />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />
        
        <main className="flex-1 overflow-y-auto p-6">
          {/* Cards de Estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Total de Veículos */}
            <div className="card hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-400 text-sm mb-1">Total de Veículos</p>
                  <h3 className="text-3xl font-bold text-white">{stats.total}</h3>
                </div>
                <div className="w-12 h-12 rounded-full bg-primary-500/20 flex items-center justify-center">
                  <Car className="w-6 h-6 text-primary-400" />
                </div>
              </div>
            </div>

            {/* Veículos Ativos */}
            <div className="card hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-400 text-sm mb-1">Ativos</p>
                  <h3 className="text-3xl font-bold text-success-400">{stats.active}</h3>
                </div>
                <div className="w-12 h-12 rounded-full bg-success-500/20 flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-success-400" />
                </div>
              </div>
            </div>

            {/* Veículos Offline */}
            <div className="card hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-400 text-sm mb-1">Offline</p>
                  <h3 className="text-3xl font-bold text-warning-400">{stats.offline}</h3>
                </div>
                <div className="w-12 h-12 rounded-full bg-warning-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-warning-400" />
                </div>
              </div>
            </div>

            {/* Alertas */}
            <div className="card hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-dark-400 text-sm mb-1">Alertas</p>
                  <h3 className="text-3xl font-bold text-danger-400">{stats.alerts}</h3>
                </div>
                <div className="w-12 h-12 rounded-full bg-danger-500/20 flex items-center justify-center">
                  <Bell className="w-6 h-6 text-danger-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Conteúdo Principal */}
          <div className={`grid grid-cols-1 gap-6 transition-all duration-300 ${rightPanelOpen ? 'lg:grid-cols-3' : 'lg:grid-cols-1'}`}>
            {/* Mapa */}
            <div className={rightPanelOpen ? 'lg:col-span-2' : 'lg:col-span-1'}>
              <div className="relative">
                <MapView layoutVersion={rightPanelOpen ? 1 : 0} />
                <button
                  type="button"
                  onClick={() => setRightPanelOpen((open) => !open)}
                  className="absolute right-4 top-4 z-[500] flex h-11 w-11 items-center justify-center rounded-lg border border-dark-700 bg-dark-900/95 text-dark-200 shadow-xl shadow-black/30 transition-colors hover:border-primary-500 hover:bg-dark-800 hover:text-white"
                  aria-label={rightPanelOpen ? 'Recolher painel lateral direito' : 'Mostrar painel lateral direito'}
                  title={rightPanelOpen ? 'Recolher painel' : 'Mostrar painel'}
                >
                  {rightPanelOpen ? (
                    <ChevronRight className="h-5 w-5" />
                  ) : (
                    <ChevronLeft className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Painel Lateral */}
            {rightPanelOpen && (
              <div className="space-y-6">
                <VehiclePanel vehicles={vehicles} />
                <AlertsPanel />
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
